import { getRowLabel, type AnchoredRow } from '../types/anchoredRowTypes'

export interface LedgerSafeHiddenWitnessRowRun {
  rowIds: string[]
  rowCount: number
  firstRowLabel: string
  lastRowLabel: string
  label: string
  supportingWitnessIds: string[]
}

export interface DeriveLedgerSafeHiddenWitnessRowRunsOptions {
  rows: readonly AnchoredRow[]
  visibleWitnessIds: readonly string[]
  hiddenWitnessIds: readonly string[]
  protectedRowIds?: ReadonlySet<string>
}

const compareRows = (left: AnchoredRow, right: AnchoredRow): number => (
  left.position - right.position
  || left.labelNumber - right.labelNumber
  || left.id.localeCompare(right.id)
)

const activeWitnessIds = (row: AnchoredRow): string[] => Array.from(new Set(
  (row.unitMappings || [])
    .filter(mapping => !mapping.excluded && Boolean(mapping.witnessId))
    .map(mapping => mapping.witnessId),
))

export const isLedgerSafeHiddenWitnessOnlyRow = ({
  row,
  visibleWitnessIds,
  hiddenWitnessIds,
  protectedRowIds = new Set<string>(),
}: {
  row: AnchoredRow
  visibleWitnessIds: ReadonlySet<string>
  hiddenWitnessIds: ReadonlySet<string>
  protectedRowIds?: ReadonlySet<string>
}): boolean => {
  if (protectedRowIds.has(row.id)) {
    return false
  }

  const witnessIds = activeWitnessIds(row)
  return witnessIds.length > 0
    && !witnessIds.some(witnessId => visibleWitnessIds.has(witnessId))
    && witnessIds.every(witnessId => hiddenWitnessIds.has(witnessId))
}

/** Display anchors only. Never rewrite persisted structural boundaries. */
export const projectLedgerHeadersPastHiddenRows = <T>(
  entries: readonly { header: T; rowIds: readonly string[] }[],
  hiddenRowIds: ReadonlySet<string>,
): Map<string, T[]> => {
  const result = new Map<string, T[]>()
  entries.forEach(({ header, rowIds }) => {
    const anchor = rowIds.find(id => !hiddenRowIds.has(id))
    if (!anchor) return
    const headers = result.get(anchor) || []
    headers.push(header)
    result.set(anchor, headers)
  })
  return result
}

export const deriveLedgerSafeHiddenWitnessRowRuns = ({
  rows,
  visibleWitnessIds,
  hiddenWitnessIds,
  protectedRowIds = new Set<string>(),
}: DeriveLedgerSafeHiddenWitnessRowRunsOptions): LedgerSafeHiddenWitnessRowRun[] => {
  const visibleSet = new Set(visibleWitnessIds)
  const hiddenSet = new Set(hiddenWitnessIds)
  const hiddenOrder = new Map(hiddenWitnessIds.map((witnessId, index) => [witnessId, index]))
  const sortedRows = [...rows].sort(compareRows)
  const runs: LedgerSafeHiddenWitnessRowRun[] = []

  let index = 0
  while (index < sortedRows.length) {
    const row = sortedRows[index]
    if (!isLedgerSafeHiddenWitnessOnlyRow({
      row,
      visibleWitnessIds: visibleSet,
      hiddenWitnessIds: hiddenSet,
      protectedRowIds,
    })) {
      index += 1
      continue
    }

    const runStartIndex = index
    let runEndIndex = index
    while (
      runEndIndex + 1 < sortedRows.length
      && isLedgerSafeHiddenWitnessOnlyRow({
        row: sortedRows[runEndIndex + 1],
        visibleWitnessIds: visibleSet,
        hiddenWitnessIds: hiddenSet,
        protectedRowIds,
      })
    ) {
      runEndIndex += 1
    }

    const runRows = sortedRows.slice(runStartIndex, runEndIndex + 1)
    const supportingWitnessIds = Array.from(new Set(runRows.flatMap(activeWitnessIds)))
      .sort((left, right) => (
        (hiddenOrder.get(left) ?? Number.MAX_SAFE_INTEGER)
        - (hiddenOrder.get(right) ?? Number.MAX_SAFE_INTEGER)
        || left.localeCompare(right)
      ))
    const firstRowLabel = getRowLabel(runRows[0])
    const lastRowLabel = getRowLabel(runRows[runRows.length - 1])

    runs.push({
      rowIds: runRows.map(candidate => candidate.id),
      rowCount: runRows.length,
      firstRowLabel,
      lastRowLabel,
      label: runRows.length === 1 ? firstRowLabel : `${firstRowLabel}–${lastRowLabel}`,
      supportingWitnessIds,
    })
    index = runEndIndex + 1
  }

  return runs
}
